import { useState, useEffect, useCallback, useRef } from 'react';
import { useIncidents } from '../../../hooks/useIncidents';
import { defaultFilterIncidents, exportToExcelGeneral } from '../../../utils/Common';

const STORAGE_KEY = 'boxit_incidents_list_filters';
const DEFAULT_KEYS = Object.keys(defaultFilterIncidents);

const getStoredFilters = () => {
  try {
    const raw = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(STORAGE_KEY) : null;
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const hasValidShape = DEFAULT_KEYS.every(
      (k) => Array.isArray(parsed[k]) && parsed[k].every((v) => typeof v === 'number' || typeof v === 'string')
    );
    if (!hasValidShape) return null;
    return { ...defaultFilterIncidents, ...parsed };
  } catch {
    return null;
  }
};

const useIncidentsList = () => {
  const { getIncidents } = useIncidents();
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [msgError, setMsgError] = useState('');
  const [statusIncidents, setStatusIncidents] = useState([]);
  const [exporting, setExporting] = useState(false);
  const abortControllerRef = useRef(null);

  const [filters, setFilters] = useState(() => getStoredFilters() ?? { ...defaultFilterIncidents });

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    } catch {
      // ignore storage errors
    }
  }, [filters]);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    const loadIncidents = async () => {
      setLoading(true);
      setError(null);
      setMsgError('');

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const liveController = new AbortController();
      abortControllerRef.current = liveController;

      try {
        const response = await getIncidents(
          {
            ...filters,
            isExported: false,
            itemsPerPage,
            pageNumber: currentPage,
          },
          abortControllerRef.current.signal
        );

        const payload = response?.payload ?? [];
        setIncidents(Array.isArray(payload) ? payload : []);
        setTotalItems(response?.totalItems ?? 0);
        setStatusIncidents(Array.isArray(response?.statusCounts) ? response.statusCounts : []);

        if (response?.code === 400) {
          setMsgError('dashboard.accounting.message.NO_INCIDENTS_DATEMAX');
        } else if (response?.code === 200 && (response?.totalItems ?? 0) === 0) {
          setMsgError('dashboard.accounting.message.NO_INCIDENTS_FILTERS');
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error loading incidents:', err);
          setError(err.message || 'Error al cargar incidentes');
          setIncidents([]);
          setTotalItems(0);
          setStatusIncidents([]);
        }
      } finally {
        // Only the request that was not superseded may clear the flag: an
        // aborted one used to switch it off for its replacement, blanking the
        // view until the new data arrived.
        if (!liveController.signal.aborted) setLoading(false);
      }
    };

    loadIncidents();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [getIncidents, filters, currentPage, itemsPerPage]);

  const handleFilter = useCallback((newFilters) => {
    const hasAnyFilter = Object.entries(newFilters || {}).some(
      ([, value]) => Array.isArray(value) && value.length > 0
    );

    const normalizedFilters = hasAnyFilter ? newFilters : { ...defaultFilterIncidents };

    setFilters(normalizedFilters);
    setCurrentPage(1);
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters({ ...defaultFilterIncidents });
    setCurrentPage(1);
  }, []);

  const handleStatusClick = useCallback((statusId) => {
    setFilters((prev) => ({
      ...prev,
      statusIncident: [statusId],
      startDate: [],
      endDate: [],
    }));
    setCurrentPage(1);
  }, []);

  const handleSort = useCallback(() => {
    // Server-side list uses ORDER BY IdIncidents DESC; column sort deferred to a future iteration.
  }, []);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleItemsPerPageChange = useCallback((items) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  }, []);

  const handleExportIncidents = useCallback(async () => {
    if (!totalItems && !loading) {
      return { success: false, errorKey: 'dashboard.accounting.message.NO_INCIDENTS_TO_EXPORT' };
    }
    setMsgError('');
    setExporting(true);
    try {
      const ac = new AbortController();
      const response = await getIncidents({ ...filters, isExported: true }, ac.signal);
      const data = response?.payload ?? [];
      if (response?.code === 400) {
        setMsgError('dashboard.accounting.message.NO_INCIDENTS_DATEMAX');
        return { success: false };
      }
      if (response?.code === 404) {
        setMsgError('dashboard.accounting.message.NO_INCIDENTS_FILTERS');
        return { success: false };
      }
      const d = new Date();
      const formattedDate = `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
      exportToExcelGeneral(`reportIncident_${formattedDate}`, 'report', Array.isArray(data) ? data : []);
      return { success: true };
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error(err);
        return { success: false };
      }
      return { success: false };
    } finally {
      setExporting(false);
    }
  }, [getIncidents, filters, totalItems, loading]);

  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;

  return {
    incidents,
    loading,
    error,
    msgError,
    statusIncidents,
    filters,
    handleFilter,
    handleResetFilters,
    handleStatusClick,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleExportIncidents,
    exporting,
    pagination: {
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage,
    },
  };
};

export default useIncidentsList;
